/**
 * A serial line and the two verbs every serial carries (redesign §47.5), shared
 * by an organization's page and an account's: a frame on the hairline, radius
 * 12, on a 64px · 1fr · auto grid — the serial in mono, to whom, a secondary
 * phrase — with at most two actions in view. Where a line has three (Beta N
 * days, Grant 7 days, Revoke), Revoke goes into the row's « More actions »
 * menu, red and last; where it has two it stays a red link. An action without a
 * modal says its effect on its own line, and a revoked serial keeps no action.
 */
import { useState } from 'react';
import type React from 'react';
import { color, font } from '../theme/tokens';
import { GhostButton, RowAction, StatusCell } from '../components/primitives';
import { useConfirm } from '../components/Modal';
import { MenuButton } from '../components/MenuButton';
import { api, ApiError } from '../lib/api';

export interface LineNote {
  serial: number;
  text: string;
  ok: boolean;
}

export interface SerialActions {
  /** The confirmation the revocation asks through; render it once on the page. */
  dialog: React.ReactNode;
  run: (action: 'courtesy' | 'revoke', serial: number) => Promise<void>;
  /** What an action without a modal did, for the line of that serial. */
  noteFor: (serial: number | null) => LineNote | null;
  /** The reason a serial was revoked from this page, if it was. */
  revokedFor: (serial: number | null) => string | undefined;
}

/**
 * The two actions that change something. Both name a serial, both are recorded
 * with who ran them, and the destructive one asks for a reason — because "why
 * did this stop working?" is the question it creates.
 */
export function useSerialActions(reload: () => Promise<void>): SerialActions {
  const [dialog, , askFor] = useConfirm();
  const [note, setNote] = useState<LineNote | null>(null);
  const [revoked, setRevoked] = useState<Record<number, string>>({});

  const run = async (action: 'courtesy' | 'revoke', serial: number): Promise<void> => {
    // The reason is not decoration: it is written into the licence row and read
    // back on this page and in the trail, for as long as the serial exists. So
    // it is asked for in a field with a label rather than in a grey box with
    // the origin printed above it, and the verb cannot be pressed while it is
    // empty — which is the same refusal the old `window.prompt` made by
    // returning an empty string, said before the click instead of after it.
    let reason = '';
    if (action === 'revoke') {
      const why = await askFor({
        title: `Revoke serial #${serial}?`,
        lede:
          'The key stops working at the next request the app makes, wherever it is installed. The row and its serial ' +
          'are kept, so the trail stays readable — and a revocation is not undone by the next invoice.',
        label: 'Why is it being revoked?',
        initial: 'refund after withdrawal',
        confirm: 'Revoke it',
        danger: true
      });
      if (why === null) return;
      reason = why;
    }
    try {
      const answer = await api.post<{ message: string }>('/admin/actions', { action, serial, reason });
      if (action === 'revoke') setRevoked((was) => ({ ...was, [serial]: reason }));
      else setNote({ serial, text: answer.message, ok: true });
      await reload();
    } catch (failure) {
      setNote({ serial, text: failure instanceof ApiError ? failure.message : 'That did not work.', ok: false });
    }
  };

  return {
    dialog,
    run,
    noteFor: (serial) => (serial !== null && note?.serial === serial ? note : null),
    revokedFor: (serial) => (serial !== null ? revoked[serial] : undefined)
  };
}

/** One serial's frame: the serial, to whom, and its actions on the right. */
export function SerialLine({
  serial,
  who,
  detail,
  actions,
  beta
}: {
  serial: number | null;
  /** To whom: an address or an organization, then the role in grey. */
  who: React.ReactNode;
  /** A secondary phrase under it, a refusal included. */
  detail?: React.ReactNode;
  actions: SerialActions;
  /** The line's beta verb, where it has one: it comes first and counts as one of the actions. */
  beta?: React.ReactNode;
}): React.JSX.Element {
  const gone = actions.revokedFor(serial);
  const said = actions.noteFor(serial);
  const count = (beta ? 1 : 0) + (serial !== null ? 2 : 0);
  return (
    <div
      className="al-serial-line"
      style={{
        display: 'grid',
        gridTemplateColumns: '64px minmax(0,1fr) auto',
        gap: 12,
        alignItems: 'center',
        border: `1px solid ${color.line}`,
        borderRadius: 12,
        padding: '12px 14px'
      }}
    >
      <span style={{ fontFamily: font.mono, fontSize: 12.5, color: serial ? color.ink : color.inkGhost }}>
        {serial ? `#${serial}` : 'no key'}
      </span>
      <span style={{ fontSize: 13.5, minWidth: 0, overflowWrap: 'anywhere' }}>
        {who}
        {detail}
        {gone !== undefined && (
          <span style={{ display: 'block', marginTop: 4 }}>
            <StatusCell tone="bad" word={`Revoked just now — ${gone}`} />
          </span>
        )}
        {said && (
          <span
            className={said.ok ? 'al-line-lit' : undefined}
            style={{ display: 'block', fontSize: 13, marginTop: 4, color: said.ok ? color.okText : color.badText }}
          >
            {said.text}
          </span>
        )}
      </span>
      {gone === undefined && (
        <span style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {beta}
          {serial !== null && (
            <GhostButton size="sm" onClick={() => void actions.run('courtesy', serial)}>
              Grant 7 days
            </GhostButton>
          )}
          {serial !== null &&
            (count > 2 ? (
              <MenuButton
                icon
                align="right"
                label="More actions"
                items={[{ label: 'Revoke', danger: true, onPick: () => void actions.run('revoke', serial) }]}
              />
            ) : (
              <RowAction danger onClick={() => void actions.run('revoke', serial)}>
                Revoke
              </RowAction>
            ))}
        </span>
      )}
    </div>
  );
}
